/**
 * SuppressionSection — iter85 (CAN-SPAM suppression list, Platform scope)
 *
 * Emails here are never sent anything again — enforced centrally in
 * email_service._guarded_send. Populated automatically by executed
 * privacy requests; manual add/remove for ops corrections.
 */
import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { MailX, Loader2, ChevronDown, ChevronRight, Plus, Trash2 } from "lucide-react";

export default function SuppressionSection() {
    const [open, setOpen] = useState(false);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            const r = await api.suppressionList();
            setRows(r.rows);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to load suppression list");
        }
        setLoading(false);
    }, []);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { if (open) load(); }, [open, load]);

    const add = async () => {
        const e = email.trim().toLowerCase();
        if (!e.includes("@")) { toast.error("Enter a valid email"); return; }
        setBusy(true);
        try {
            await api.suppressionAdd(e, "manual");
            toast.success(`${e} suppressed`);
            setEmail("");
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to suppress");
        }
        setBusy(false);
    };

    const remove = async (e) => {
        try {
            // iter87.71 — Prefer the audited `unsuppress` endpoint (writes
            // a `compliance_actions` row + platform_audit_log entry).
            // Falls back to the legacy `suppressionRemove` for cases where
            // the operator lacks platform-admin scope.
            try {
                await api.privacyUnsuppress(e);
            } catch (primary) {
                if (primary?.response?.status === 403 || primary?.response?.status === 401) {
                    await api.suppressionRemove(e);
                } else {
                    throw primary;
                }
            }
            toast.success(`${e} removed from suppression`);
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to remove");
        }
    };

    return (
        <section className="brutal-card" data-testid="suppression-section">
            <button
                type="button"
                data-testid="suppression-toggle"
                onClick={() => setOpen((v) => !v)}
                className="w-full p-4 flex items-center gap-3 text-left hover:bg-[#0a0a0a]/40 transition-colors"
            >
                {open ? <ChevronDown className="w-4 h-4 text-cyber shrink-0" /> : <ChevronRight className="w-4 h-4 text-zinc-500 shrink-0" />}
                <MailX className="w-5 h-5 text-cyber shrink-0" />
                <div className="flex-1">
                    <h3 className="font-display font-extrabold text-base uppercase">Email Suppression List</h3>
                    <p className="text-[11px] text-zinc-500">
                        CAN-SPAM enforcement — suppressed addresses never receive invites or announcements.
                    </p>
                </div>
                <span className="pill text-[10px] font-bold uppercase border-[#333] text-zinc-400" data-testid="suppression-count-pill">
                    {loading ? "…" : `${rows.length} suppressed`}
                </span>
            </button>

            {open && (
                <div className="p-4 pt-0 space-y-3">
                    <div className="flex gap-2 items-stretch">
                        <input
                            data-testid="suppression-add-email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="Manually suppress an email…"
                            className="brutal-input text-xs flex-1"
                        />
                        <button
                            type="button"
                            onClick={add}
                            disabled={busy}
                            data-testid="suppression-add-btn"
                            className="brutal-btn-primary text-xs py-2 disabled:opacity-60"
                        >
                            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                            Suppress
                        </button>
                    </div>

                    {rows.length === 0 && !loading ? (
                        <p className="text-xs text-zinc-500 italic text-center p-4" data-testid="suppression-empty">
                            No suppressed emails.
                        </p>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-xs" data-testid="suppression-table">
                                <thead className="bg-[#0a0a0a] border-b-2 border-[#333]">
                                    <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500">
                                        <th className="px-3 py-2 font-bold">Email</th>
                                        <th className="px-3 py-2 font-bold">Reason</th>
                                        <th className="px-3 py-2 font-bold">Since</th>
                                        <th className="px-3 py-2 font-bold">By</th>
                                        <th className="px-3 py-2" />
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((row) => (
                                        <tr key={row.email} className="border-b border-[#222]" data-testid={`suppression-row-${row.email}`}>
                                            <td className="px-3 py-2 font-mono text-white">{row.email}</td>
                                            <td className="px-3 py-2 text-zinc-400">{row.reason}</td>
                                            <td className="px-3 py-2 font-mono text-zinc-500 whitespace-nowrap">
                                                {(row.suppressed_at || "").slice(0, 10)}
                                            </td>
                                            <td className="px-3 py-2 text-zinc-400">{row.suppressed_by || "—"}</td>
                                            <td className="px-3 py-2 text-right">
                                                <button
                                                    type="button"
                                                    onClick={() => remove(row.email)}
                                                    data-testid={`suppression-remove-${row.email}`}
                                                    className="brutal-btn-ghost text-[10px] py-1 text-red-400"
                                                >
                                                    <Trash2 className="w-3 h-3" /> Remove
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}
        </section>
    );
}
